import moment from 'moment-timezone';

import { WORK_ASSIGNMENT_ACTIVITY_TYPE } from 'constants/activityTypes';

export const SHIFT_GROUP_TYPENAME = 'ShiftGroup';

const SHIFT_TAIL_TYPENAME = 'ShiftTail';
const REJECTED_ASSIGNMENT_STATUS = 'rejected';

const buildActivityKey = (item) => {
	const activity = item?.activity ?? {};

	return [
		'base',
		activity.rideId,
		activity.shiftId,
		activity.sequenceGroupId,
		activity.activityType,
		item.fromTimestamp,
		item.toTimestamp,
	]
		.filter(Boolean)
		.join('__');
};

const buildExtraActivityKey = (extraActivity) => {
	return [
		'extra',
		extraActivity.id,
		extraActivity.shiftId,
		extraActivity.sequenceGroupId,
		extraActivity.activityType,
		extraActivity.fromTimestamp,
		extraActivity.toTimestamp,
		extraActivity.fromStation?.code,
	]
		.filter(Boolean)
		.join('__');
};

const buildShiftTailKey = ({ driverId, shiftId, fromTimestamp, toTimestamp }) => {
	return ['shiftTail', driverId, shiftId, fromTimestamp, toTimestamp].filter(Boolean).join('__');
};

const getMappedActivityShiftId = (item) => {
	return item?.activity?.shiftId ?? item?.shiftId ?? null;
};

const isHoverableActivity = (activity) =>
	activity?.__typename === WORK_ASSIGNMENT_ACTIVITY_TYPE ||
	(activity?.__typename === 'CrewMemberBreakActivityItem' && activity?.breakType === 'rest') ||
	activity?.__typename === 'CrewMemberAbsenceActivityItem';

const createShiftTailActivities = (mappedActivities = [], shifts = [], driverId) => {
	if (!Array.isArray(shifts) || shifts.length === 0) {
		return [];
	}

	return shifts.flatMap((shift) => {
		if (!shift?.id || !shift?.toTimestamp || !shift?.autoCloseTimestamp) {
			return [];
		}

		const shiftActivities = mappedActivities.filter(
			(activity) => getMappedActivityShiftId(activity) === shift.id
		);

		const lastActivityEnd = shiftActivities.reduce((latest, activity) => {
			const currentTs = activity?.toTimestamp;
			if (!currentTs) return latest;
			if (!latest) return currentTs;

			return moment(currentTs).valueOf() > moment(latest).valueOf() ? currentTs : latest;
		}, null);

		const tailStart = lastActivityEnd ?? shift.fromTimestamp;

		if (!tailStart) {
			return [];
		}

		if (moment(tailStart).isSameOrAfter(shift.toTimestamp)) {
			return [];
		}

		return [
			{
				fromTimestamp: tailStart,
				toTimestamp: shift.toTimestamp,
				activity: {
					__typename: SHIFT_TAIL_TYPENAME,
					shiftId: shift.id,
					autoCloseTimestamp: shift.autoCloseTimestamp,
				},
				key: buildShiftTailKey({
					driverId,
					shiftId: shift.id,
					fromTimestamp: tailStart,
					toTimestamp: shift.toTimestamp,
				}),
				isHoverable: false,
				isShiftTail: true,
			},
		];
	});
};

const buildBaseItem = (item, activityWithoutExtras) => ({
	...item,
	activity: activityWithoutExtras,
	key: buildActivityKey({
		...item,
		activity: activityWithoutExtras,
	}),
	isHoverable: isHoverableActivity(activityWithoutExtras),
});

const buildBaseActivityForRendering = (activity = {}, extraActivities = []) => ({
	...activity,
	extraActivities,
});

const normalizeStatus = (status) => status?.toLowerCase?.() ?? null;

const shouldHideExtraActivity = (extraActivity, parentActivity = {}) => {
	const extraStatus = normalizeStatus(extraActivity?.status);
	if (extraStatus !== REJECTED_ASSIGNMENT_STATUS) return false;

	const parentStatus = normalizeStatus(parentActivity?.status);
	return parentStatus !== REJECTED_ASSIGNMENT_STATUS;
};

const getVisibleExtraActivities = (extraActivities = [], parentActivity = {}) =>
	extraActivities.filter(
		(extraActivity) => !shouldHideExtraActivity(extraActivity, parentActivity)
	);

const buildExtraItem = (extraActivity, parentShiftId) => ({
	fromTimestamp: extraActivity.fromTimestamp,
	toTimestamp: extraActivity.toTimestamp,
	shiftId: parentShiftId,
	activity: {
		...extraActivity,
		__typename: WORK_ASSIGNMENT_ACTIVITY_TYPE,
		shiftId: parentShiftId,
	},
	key: buildExtraActivityKey(extraActivity),
	isHoverable: true,
	isExtraActivity: true,
});

const getItemShiftId = (item) => item?.activity?.shiftId ?? item?.shiftId ?? null;

const withPseudoShiftGroupId = (item, pseudoShiftGroupId) => ({
	...item,
	pseudoShiftGroupId,
});

const buildPseudoShiftGroupId = (baseItem) =>
	`pseudoShift__${baseItem.key || buildActivityKey(baseItem)}`;

const buildShiftGroup = (shift, children) => {
	const sortedChildren = [...children].sort(
		(a, b) => moment(a.fromTimestamp).valueOf() - moment(b.fromTimestamp).valueOf()
	);

	return {
		__typename: SHIFT_GROUP_TYPENAME,
		fromTimestamp: shift.fromTimestamp,
		toTimestamp: shift.toTimestamp,
		shift,
		children: sortedChildren,
		key: `shiftGroup__${shift.id}`,
		isShiftGroup: true,
	};
};

const buildPseudoShiftGroup = (pseudoShiftGroupId, children) => {
	const sortedChildren = [...children].sort(
		(a, b) => moment(a.fromTimestamp).valueOf() - moment(b.fromTimestamp).valueOf()
	);
	const fromTimestamp = sortedChildren.reduce((earliest, item) => {
		if (!item.fromTimestamp) return earliest;
		if (!earliest) return item.fromTimestamp;

		return moment(item.fromTimestamp).valueOf() < moment(earliest).valueOf()
			? item.fromTimestamp
			: earliest;
	}, null);
	const toTimestamp = sortedChildren.reduce((latest, item) => {
		if (!item.toTimestamp) return latest;
		if (!latest) return item.toTimestamp;

		return moment(item.toTimestamp).valueOf() > moment(latest).valueOf()
			? item.toTimestamp
			: latest;
	}, null);

	return {
		__typename: SHIFT_GROUP_TYPENAME,
		fromTimestamp,
		toTimestamp,
		shift: {
			id: pseudoShiftGroupId,
			fromTimestamp,
			toTimestamp,
		},
		children: sortedChildren,
		key: `pseudoShiftGroup__${pseudoShiftGroupId}`,
		isShiftGroup: true,
		isPseudoShiftGroup: true,
	};
};

export const mapDriverActivities = (
	activitiesV2 = [],
	shifts = [],
	driverId,
	{ groupByShift = false } = {}
) => {
	const flatActivities = activitiesV2.flatMap((item) => {
		const { extraActivities = [], ...activityWithoutExtras } = item.activity ?? {};
		const visibleExtraActivities = getVisibleExtraActivities(
			extraActivities,
			activityWithoutExtras
		);
		const baseActivity = buildBaseActivityForRendering(
			activityWithoutExtras,
			visibleExtraActivities
		);
		const baseItem = buildBaseItem(item, baseActivity);
		const parentShiftId = item.activity?.shiftId ?? null;
		const extraItems = visibleExtraActivities.map((extra) => buildExtraItem(extra, parentShiftId));

		if (!parentShiftId && extraItems.length > 0) {
			const pseudoShiftGroupId = buildPseudoShiftGroupId(baseItem);

			return [
				withPseudoShiftGroupId(baseItem, pseudoShiftGroupId),
				...extraItems.map((extraItem) => withPseudoShiftGroupId(extraItem, pseudoShiftGroupId)),
			];
		}

		return [baseItem, ...extraItems];
	});

	const shiftTailActivities = createShiftTailActivities(flatActivities, shifts, driverId);
	const allItems = [...flatActivities, ...shiftTailActivities];

	if (!groupByShift) {
		return allItems.sort(
			(a, b) => moment(a.fromTimestamp).valueOf() - moment(b.fromTimestamp).valueOf()
		);
	}

	const shiftIds = new Set(shifts.map((s) => s.id));
	const childrenByShiftId = new Map(shifts.map((s) => [s.id, []]));
	const childrenByPseudoShiftId = new Map();
	const orphanItems = [];

	allItems.forEach((item) => {
		const sId = getItemShiftId(item);
		if (sId && shiftIds.has(sId)) {
			childrenByShiftId.get(sId).push(item);
		} else if (item.pseudoShiftGroupId) {
			const pseudoShiftChildren = childrenByPseudoShiftId.get(item.pseudoShiftGroupId) ?? [];
			pseudoShiftChildren.push(item);
			childrenByPseudoShiftId.set(item.pseudoShiftGroupId, pseudoShiftChildren);
		} else {
			orphanItems.push(item);
		}
	});

	const shiftGroups = shifts.map((shift) =>
		buildShiftGroup(shift, childrenByShiftId.get(shift.id))
	);
	const pseudoShiftGroups = [...childrenByPseudoShiftId.entries()].map(
		([pseudoShiftId, children]) => buildPseudoShiftGroup(pseudoShiftId, children)
	);

	return [...shiftGroups, ...pseudoShiftGroups, ...orphanItems].sort(
		(a, b) => moment(a.fromTimestamp).valueOf() - moment(b.fromTimestamp).valueOf()
	);
};
